<template>
  <v-container class="modern-master-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-boxes</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Catálogo de Artículos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión de maestro de materiales y servicios</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <Can I="create" a="StockArticle">
          <div class="d-flex align-center gap-3">
            <v-btn 
              color="primary" 
              outlined 
              class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-xs" 
              @click="$router.push({name:'ImportStock'})"
            >
              <v-icon left small>fas fa-file-import</v-icon> Importar
            </v-btn>
            <v-btn 
              color="success" 
              class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm" 
              @click="update(-1)"
            >
              <v-icon left small>fas fa-plus</v-icon> Nuevo Artículo
            </v-btn>
          </div>
        </Can>
      </div>
    </v-row>

    <!-- Filter Section (Master Pattern) -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1 px-1">Filtrar por Grupo / Familia</div>
          <v-select
            v-model="family"
            :items="families"
            item-text="name"
            item-value="id"
            placeholder="Todas las Familias"
            outlined dense hide-details
            clearable
            :disabled="loading"
            class="modern-input"
            prepend-inner-icon="fas fa-filter"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1">
          <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="articles"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Slot (Standardized) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Artículos Registrados: {{total}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por código o nombre..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details
              clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <!-- Cell Templates -->
        <template v-slot:item.sku="{ item }">
          <span class="body-2 font-weight-black primary--text tracking-tighter">{{item.sku}}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <div class="d-flex flex-column">
              <span class="body-2 font-weight-bold grey--text text--darken-3 lh-1-2 mb-1">{{item.name}}</span>
              <div v-if="item.cost" class="d-flex align-center">
                <v-chip x-small color="info lighten-5" label class="info--text font-weight-black px-2">
                  <v-icon x-small class="mr-1">fas fa-link</v-icon>
                  {{item.cost.name}}
                </v-chip>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.family_name="{ item }">
          <span class="caption grey--text text--darken-2 font-weight-bold uppercase">{{item.family_name}}</span>
        </template>

        <template v-slot:item.unit_name="{ item }">
          <v-chip x-small outlined color="grey" class="font-weight-bold uppercase">{{item.unit_name}}</v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <Can I="update" a="StockArticle">
            <v-switch 
              v-model="item.status" 
              hide-details 
              dense 
              class="mt-0 pt-0 modern-switch"
              @change="updateStatus(item)"
              color="success"
            ></v-switch>
          </Can>
        </template>

        <template v-slot:item.actions="{ item }">
          <Can I="update" a="StockArticle">
            <v-btn icon small color="primary" class="grey lighten-4 shadow-xs" @click.stop="update(item.id)">
              <v-icon x-small>fas fa-edit</v-icon>
            </v-btn>
          </Can>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Update Dialog -->
    <v-dialog v-model="showDialog" max-width="1000" overlay-color="primary" overlay-opacity="0.2">
      <v-card class="modern-card pa-0 overflow-hidden">
        <div class="primary pa-6 d-flex align-center">
          <v-icon color="white" class="mr-4">fas fa-box-open</v-icon>
          <span class="text-h5 font-weight-black white--text">{{ article > 0 ? 'Editar Artículo' : 'Nuevo Artículo' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showDialog=false" dark>
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>
        <div class="pa-8">
          <article-create-update 
            :families="families" 
            :units="units" 
            :id="article"
            @updated="fetch"
            @close="showDialog=false"
          />
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {fetchArticles, fetchFamilies, fetchUnits, updateArticle} from '@/api/stocks'
import notifications from '@/utils/notifications'
import ArticleCreateUpdate from '@/components/stocks/ArticleCreateUpdate'

export default {
  name:'Articles',
  components:{ArticleCreateUpdate},
  data() {
    return {
      loading: false,
      family:'',
      families:[],
      units:[],
      articles:[],
      article:'',
      timer:null,
      options:{},
      search:'',
      total:0,
      showDialog:false,
      headers: [
        { text: 'Código', align: 'start', value: 'sku', class: 'table-header', width: '15%' },
        { text: 'Nombre y Configuración', align: 'start', value: 'name', class: 'table-header', width: '35%' },
        { text: 'Familia', align: 'start', value: 'family_name', class: 'table-header' },
        { text: 'Unidad', align: 'start', value: 'unit_name', class: 'table-header' },
        { text: 'Estatus', align: 'center', value: 'status', class: 'table-header', sortable: false },
        { text: 'Acciones', value: 'actions', class: 'table-header', align: 'center', sortable: false }
      ],
    }
  },
  methods:{
    async fetch(){
      this.loading=true
      const {sortBy, sortDesc, page, itemsPerPage }=this.options
      let sort=''
      if(sortBy && sortBy[0]=='family_name'){
        sort = 'family__name'
      }
      else if(sortBy && sortBy[0]=='unit_name'){
        sort = 'unit__name'
      }
      else {
        sort = sortBy ? sortBy[0] : 'sku'
      }
      
      const query=`ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 10}&search=${this.search || ''}&family=${this.family || ''}`
      
      await fetchArticles(query)
        .then(res=>{
          this.total=res.count
          this.articles=res.results
        })
        .catch((err)=>{
          notifications.showError('Error cargando artículos '+err)
        })
        .finally(()=>{
          this.loading=false
        })
    },
    searchTimeOut() {  
      if (this.timer) {
          clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
          this.fetch()
      }, 700);
    },
    update(item){
      this.article=item
      this.showDialog=true
    },
    async updateStatus(item){
      await updateArticle(item.id, {status: item.status})
        .then(()=>{
          const state = item.status ? 'Activado' : 'Desactivado'
          notifications.showSuccess(`${item.name} ha sido ${state.toLowerCase()} correctamente`)
        })
        .catch((e)=>{
          notifications.showError('Error al actualizar el estatus: ' + e)
          // Revertimos el estado en la interfaz si falla la API
          item.status = !item.status
        })
    },
  },
  watch:{
    async family(){
      this.options.page = 1
      this.fetch()
    },
    options:{
      handler:'fetch',
      deep:true
    }
  },
  async mounted(){
    this.families=await fetchFamilies()
    this.units=await fetchUnits()
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 2px 4px rgba(0,0,0,0.03) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-search-field { width: 320px; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; background-color: white !important; }
.modern-search-field >>> .v-input__control { box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; border-bottom: 2px solid #eef2f5 !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; }

.modern-switch >>> .v-input--selection-controls__ripple { color: var(--v-success-base) !important; }
</style>